import { h, Host } from "@stencil/core";
import { environmentToIssuer, links } from "../../data/links";
import { getShouldBeIgnored } from "../../util/html-util";
import localI18n, { i18nInit } from "../../util/i18n";
import { JwtUtil } from "../../util/jwt-util";
export class ProfileMenu {
    constructor() {
        this.showMenuButton = false;
        this.showFlyout = false;
        this.actingType = undefined;
        this.niveau = undefined;
        this.isStandardNoIdentity = false;
        this.userEmail = undefined;
        this.currentLanguage = 'de';
        this.links = undefined;
        this.groups = [];
        this.fachschluessel = undefined;
        this.isVertreterProfil = false;
    }
    flyout;
    button;
    profileName;
    kommtEventuellSpaeterDannwiederAusbauen = false;
    observer;
    el;
    /**
     * Request a token event form the oiam-oauth-wc
     */
    oiamSendTokenEvent;
    /**
     * Communicate logout-intent to oiam-oauth-wc
     */
    oiamLogoutEvent;
    /**
     * Send login-Event to oiam-oauth-wc
     */
    oiamLoginEvent;
    bahfSendLanguage;
    async componentWillLoad() {
        this.bahfSendLanguage.emit();
        await i18nInit(this.currentLanguage);
    }
    handleTokenEvent(ev) {
        const jwtValidationResult = JwtUtil.validateJwt(ev);
        if (jwtValidationResult.isValidJwt) {
            this.parseTokenAndSetValues(jwtValidationResult.jwt);
            this.showMenuButton = true;
        }
        else {
            // hide flyout and flyout button if logged-out (idle timeout, reload from back-forward-cache etc.)
            // oiam-oauth-wc sends an oiamTokenEvent with empty jwt in that cases
            if (this.showFlyout) {
                this.hideFlyout();
            }
            this.showMenuButton = false;
        }
    }
    closeFlyoutWhenTabbingOut(event) {
        if (event.key !== 'Tab') {
            return;
        }
        const path = event.composedPath();
        const flyout = this.el.shadowRoot.getElementById('headerFlyoutProfile');
        if (!path.includes(flyout) && this.showFlyout) {
            this.hideFlyout();
        }
    }
    handleClickEvent(event) {
        const path = event.composedPath();
        // This solves a bug. Other Teams are closing their flyouts by creating an additional click on their menu-items.
        // We need to ignore these additional Clicks. Otherwise, our flyout opens and then instantly closes.
        const shouldBeIgnored = getShouldBeIgnored(path);
        if (shouldBeIgnored) {
            return;
        }
        if (!path.includes(this.el) && this.showFlyout) {
            this.hideFlyout();
        }
    }
    handleKeyDown(ev) {
        if (ev.key === 'Escape' && this.showFlyout) {
            this.hideFlyout();
        }
    }
    handleLanguageFromHeader(ev) {
        const language = ev?.detail?.activeLanguage?.code ?? 'de';
        i18nInit(language).then(() => {
            this.currentLanguage = language;
        });
    }
    parseTokenAndSetValues(jwt) {
        this.isStandardNoIdentity = false;
        this.actingType = jwt.payload['acting-type'] ?? undefined;
        this.userEmail = jwt.payload['preferred_username'] ?? '';
        this.groups = JwtUtil.parseGroupsToValidatedArray(jwt.payload['groups']);
        let authority = jwt.payload['iss'] ?? '';
        this.links = links[authority] ?? this.handleAuthorityNotFound(authority);
        let actingOnBehalfOf = jwt.payload['acting-on-behalf-of']?.[0] ?? '';
        let identity = jwt.payload['identity'] ?? '';
        let neitherAoBoNorIdentityGiven = actingOnBehalfOf == '' && identity == '';
        if (!this.actingType) {
            this.niveau = 'bronze';
            this.isStandardNoIdentity = neitherAoBoNorIdentityGiven;
        }
        else if (!actingOnBehalfOf) {
            this.niveau = 'silver';
            this.isStandardNoIdentity = neitherAoBoNorIdentityGiven;
        }
        else {
            this.niveau = 'gold';
        }
        this.fachschluessel = JwtUtil.parseAoboToFachschluessel(actingOnBehalfOf);
        this.isVertreterProfil =
            this.actingType.toLowerCase() === 'privatperson' &&
                actingOnBehalfOf.includes('step:person') &&
                identity !== actingOnBehalfOf;
    }
    handleAuthorityNotFound(authority) {
        if (authority.includes('sso.dev.ocp.webapp.idst.ibaintern.de')) {
            return links[environmentToIssuer.get('SINT')];
        }
        return links[environmentToIssuer.get('PROD')];
    }
    componentDidLoad() {
        this.oiamSendTokenEvent.emit();
    }
    componentDidRender() {
        this.button = this.el.shadowRoot.querySelector('#profile-menu-button');
        this.flyout = this.el.shadowRoot.querySelector('#headerFlyoutProfile');
        this.profileName = this.el.shadowRoot.querySelector('#headingProfil');
        this.observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (this.isTruncated(entry)) {
                    entry.target.setAttribute('tabindex', '0');
                }
                else if (this.el.shadowRoot.activeElement !== entry.target) {
                    // nur, wenn nicht gerade fokussiert
                    entry.target.removeAttribute('tabindex');
                }
            }
        });
    }
    disconnectedCallback() {
        this.observer.disconnect();
    }
    isTruncated(entry) {
        return entry.target.scrollHeight > entry.target.clientHeight;
    }
    onButtonClick() {
        if (this.showFlyout) {
            this.hideFlyout();
        }
        else {
            this.showFlyoutContent();
            this.observer.observe(this.profileName);
        }
    }
    onLogoutClick() {
        this.oiamLogoutEvent.emit();
    }
    onProfilWechselnOderFreischaltenClick(event) {
        const isAlreadyOnPage = window.location.href.includes(this.links.zumProfil);
        if (isAlreadyOnPage) {
            // Fixes a bug: Profile-page prevents execution of href-click.
            // Fix is to submit the login-event in that case
            event.preventDefault();
            this.oiamLoginEvent.emit({ forceProfileSelection: true });
        }
    }
    showFlyoutContent() {
        this.flyout.classList.add('collapsed');
        this.flyout.classList.remove('collapse');
        this.button.classList.add('collapse');
        this.button.classList.remove('collapsed');
        this.button.ariaExpanded = 'true';
        this.showFlyout = !this.showFlyout;
    }
    hideFlyout() {
        this.observer.disconnect();
        this.flyout.classList.remove('collapsed');
        this.flyout.classList.add('collapse');
        this.button.classList.add('collapsed');
        this.button.classList.remove('collapse');
        this.button.ariaExpanded = 'false';
        this.showFlyout = !this.showFlyout;
    }
    render() {
        if (this.showMenuButton) {
            return (h(Host, { key: '0414377e5b92d5906302b62f54a7f049f0357ca5', class: 'd-block rounded' }, h("div", { key: '76ce95ba989e4c60109db248c43770fe3147bad5', class: "ba-header-element" }, this.getMenuEntry(), this.getFlyOut())));
        }
    }
    getMenuEntry() {
        const baseClasses = 'ba-menu-entry ba-profile ba-login collapsed me-0';
        const vertreterClass = this.isVertreterProfil ? 'ba-vertreter-icon' : '';
        const combinedClasses = `${baseClasses} ${vertreterClass}`;
        return (h("button", { id: "profile-menu-button", class: combinedClasses, title: this.actingType ? localI18n.t('PROFIL') : localI18n.t('KONTO'), type: "button", onClick: () => this.onButtonClick(), "data-bs-target": "#headerFlyoutProfile", "data-bs-toggle": "collapse", "aria-expanded": "false", "aria-controls": "headerFlyoutProfile" }, h("span", { class: "ba-title" }, this.actingType ? localI18n.t('PROFIL') : localI18n.t('KONTO'))));
    }
    getFlyOut() {
        return (h("div", { class: "ba-flyout-container ba-flyout-profile" }, h("div", { id: "headerFlyoutProfile", class: "ba-flyout-collapse collapse", "data-bs-parent": ".ba-header" }, h("div", { class: "ba-flyout ba-menu-container", "aria-labelledby": "headingProfil" }, h("h2", { class: "h4", id: "headingProfil", "data-tooltip": this.getHeading() }, this.getHeading()), h("hr", { "aria-hidden": "true" }), h("ul", { class: "ba-linklist" }, this.niveau === 'bronze' ? this.getBronze() : null, this.niveau === 'silver' ? this.getSilver() : null, this.niveau === 'gold' ? this.getGold() : null)))));
    }
    getHeading() {
        if (this.niveau === 'gold') {
            let schluesselTypTitle;
            switch (this.fachschluessel.schluesselTyp) {
                case 'kundennummer':
                    schluesselTypTitle = localI18n.t('KUNDENNUMMER');
                    break;
                case 'kindergeldnummer':
                    schluesselTypTitle = localI18n.t('KINDERGELDNUMMER');
                    break;
                case 'partnerid':
                    schluesselTypTitle = localI18n.t('PARTNERNUMMER');
                    break;
                case 'mitarbeiterid':
                    schluesselTypTitle = localI18n.t('KUNDENNUMMER');
            }
            return `${schluesselTypTitle}:\n${this.fachschluessel.schluessel}`;
        }
        else {
            return this.userEmail.toLowerCase();
        }
    }
    getBronze() {
        return [this.getProfilWechseln(), this.getDivider(), this.getKontoEinstellungen(), this.getAbmelden()];
    }
    getSilver() {
        return [
            this.getZumProfil(),
            this.getProfilBearbeiten(),
            this.getProfilWechseln(),
            this.getDivider(),
            this.getKontoEinstellungen(),
            this.getAbmelden(),
        ];
    }
    getGold() {
        return [
            this.getZumProfil(),
            this.getProfilBearbeiten(),
            this.getProfilWechseln(),
            this.getPostfaecher(),
            this.getDivider(),
            this.getKontoEinstellungen(),
            this.getAbmelden(),
        ];
    }
    getPostfaecher() {
        const istBetrieb = this.fachschluessel.objektTyp === 'betrieb';
        const istKundennummer = this.fachschluessel.schluesselTyp === 'kundennummer';
        const istKindergeldnummer = this.fachschluessel.schluesselTyp === 'kindergeldnummer';
        const istZuwanderer = this.groups.includes('zuwanderungsinteressierter');
        if (istZuwanderer) {
            return [this.getDivider(), this.getPostfachFuerZuwanderer()];
        }
        if (istBetrieb) {
            return [
                this.getDivider(),
                this.groups.includes('admin') ? this.getLeistungsPostfach() : null,
                this.getVermittlungsPostfach(),
            ];
        }
        if (istKundennummer && !istBetrieb) {
            return [this.getDivider(), this.getLeistungsPostfach(), this.getVermittlungsPostfach()];
        }
        // Kindergeldnummer (nur wenn Flag gesetzt)
        if (this.kommtEventuellSpaeterDannwiederAusbauen && istKindergeldnummer) {
            return [this.getDivider(), this.getPostfach()];
        }
    }
    getPostfach() {
        return (h("li", null, h("a", { href: this.links['postfach'], class: "ba-link-icon ba-icon-message" }, localI18n.t('POSTFACH'))));
    }
    getPostfachFuerZuwanderer() {
        return (h("li", null, h("a", { href: this.links['leistungsPostfach'], class: "ba-link-icon ba-icon-message" }, localI18n.t('POSTFACH'))));
    }
    getLeistungsPostfach() {
        return (h("li", null, h("a", { href: this.links['leistungsPostfach'], class: "ba-link-icon ba-icon-message" }, localI18n.t('LEISTUNGSPOSTFACH'))));
    }
    getVermittlungsPostfach() {
        return (h("li", null, h("a", { href: this.links['vermittlungsPostfach'], class: "ba-link-icon ba-icon-message" }, localI18n.t('VERMITTLUNGS_POSTFACH'))));
    }
    getDivider() {
        return (h("li", { "aria-hidden": "true" }, h("hr", null)));
    }
    getProfilWechseln() {
        if (this.groups.includes('zuwanderungsinteressierter')) {
            return null;
        }
        let text;
        let icon;
        if (this.isStandardNoIdentity) {
            text = localI18n.t('PROFIL_FREISCHALTEN');
            icon = 'ba-icon-plus';
        }
        else {
            text = localI18n.t('PROFIL_WECHSELN');
            icon = 'ba-icon-change';
        }
        return (h("li", null, h("a", { id: "profileProfilWechseln", href: `${this.links['zumProfil']}?force-profile-selection=true`, class: `ba-link-icon ${icon}`, onClick: (event) => this.onProfilWechselnOderFreischaltenClick(event) }, text)));
    }
    getProfilBearbeiten() {
        return (h("li", null, h("a", { href: this.links['profilBearbeiten'], onClick: () => this.hideFlyout(), class: "ba-link-icon ba-icon-edit" }, localI18n.t('PROFIL_BEARBEITEN'))));
    }
    getZumProfil() {
        return (h("li", null, h("a", { href: this.links['zumProfil'], onClick: () => this.hideFlyout(), class: "ba-link-icon ba-icon-home-full" }, localI18n.t('ZUM_PROFIL'))));
    }
    getKontoEinstellungen() {
        return (h("li", null, h("a", { id: 'profileKontoEinstellungen', href: this.links['kontoEinstellungen'], class: "ba-link-icon ba-icon-settings" }, localI18n.t('KONTO_EINSTELLUNGEN'))));
    }
    getAbmelden() {
        return (h("li", null, h("a", { id: 'profileLogout', onClick: (event) => {
                event.preventDefault();
                this.onLogoutClick();
            }, class: "ba-link-icon ba-icon-logout", href: "#" }, localI18n.t('ABMELDEN'))));
    }
    static get is() { return "profile-menu"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["profile-menu.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["profile-menu.css"]
        };
    }
    static get states() {
        return {
            "showMenuButton": {},
            "showFlyout": {},
            "actingType": {},
            "niveau": {},
            "isStandardNoIdentity": {},
            "userEmail": {},
            "currentLanguage": {},
            "links": {},
            "groups": {},
            "fachschluessel": {},
            "isVertreterProfil": {}
        };
    }
    static get events() {
        return [{
                "method": "oiamSendTokenEvent",
                "name": "oiamSendTokenEvent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Request a token event form the oiam-oauth-wc"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "oiamLogoutEvent",
                "name": "oiamLogoutEvent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Communicate logout-intent to oiam-oauth-wc"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "oiamLoginEvent",
                "name": "oiamLoginEvent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Send login-Event to oiam-oauth-wc"
                },
                "complexType": {
                    "original": "{ forceProfileSelection?: true; actingOnBehalfOf?: string[] }",
                    "resolved": "{ forceProfileSelection?: true; actingOnBehalfOf?: string[]; }",
                    "references": {}
                }
            }, {
                "method": "bahfSendLanguage",
                "name": "bahfSendLanguage",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get listeners() {
        return [{
                "name": "oiamTokenEvent",
                "method": "handleTokenEvent",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "keyup",
                "method": "closeFlyoutWhenTabbingOut",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "click",
                "method": "handleClickEvent",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "keydown",
                "method": "handleKeyDown",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "bahfLanguage",
                "method": "handleLanguageFromHeader",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
